import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { deallocateShiftsFromActivityOccurrence } from "@/api/Activities";
import { unassignGroupFromShift } from "@/api/Shifts";
import { useErrorMessageAlert, deducedError } from "@/Utils/ErrorUtils";
import {
  activityOccurrenceStatusSchema,
  groupStatusSchema,
  visitStatusSchema,
  type IActivityOccurrence,
  type IGroupOfClinicVisitActivityOccurrence,
} from "@medoma/models/activities";
import { type IVisit } from "@medoma/models/visits";
import { routeStatusSchema } from "@medoma/models/routes";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftWithContents,
} from "@medoma/models/shiftWithContents";

type UnassignFailureSubject = {
  visit?: IVisit | IGroupOfClinicVisitActivityOccurrence;
  activityOccurrence?: IActivityOccurrence;
};

type UnassignOperation = {
  unassign: () => Promise<void>;
  failure: UnassignFailureSubject;
};

type UnassignActivitiesAndVisitsFromShiftResult = {
  totalOperations: number;
  failedOperations: number;
  failures: Array<UnassignFailureSubject & { error: unknown }>;
};

/**
 * Frontend only implementation of clearing a shift from activities and visits
 * A backend endpoint would be much better, but this is a quick fix for now
 */
export const useUnassignActivitiesAndVisitsFromShift = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (shiftWithContents: IShiftWithContents) => {
      const result: UnassignActivitiesAndVisitsFromShiftResult = {
        failures: [],
        totalOperations: 0,
        failedOperations: 0,
      };

      const shiftId = shiftWithContents.shift.id;
      const isAssignedToShift = ({
        assignees,
      }: {
        assignees: ReadonlyArray<{ id: number }>;
      }) => assignees.some((assignee) => assignee.id === shiftId);

      const runUnassign = async ({ unassign, failure }: UnassignOperation) => {
        try {
          await unassign();
        } catch (error) {
          result.failures.push({ ...failure, error });
          result.failedOperations++;
        }
        result.totalOperations++;
      };

      for (const shiftItem of shiftWithContents.items.map(classifyShiftItem)) {
        switch (shiftItem.kind) {
          case shiftItemKindSchema.enum.route: {
            const route = shiftItem.item;
            if (route.status === routeStatusSchema.enum.finished) {
              break;
            }
            for (const visit of route.visits) {
              if (
                visit.status !== visitStatusSchema.enum.finished &&
                isAssignedToShift(visit)
              ) {
                await runUnassign({
                  unassign: () => unassignGroupFromShift(visit.id, shiftId),
                  failure: { visit },
                });
              }
            }
            break;
          }
          case shiftItemKindSchema.enum.group: {
            const group = shiftItem.item;
            if (
              group.visitStatus !== groupStatusSchema.enum.finished &&
              isAssignedToShift(group)
            ) {
              await runUnassign({
                unassign: () => unassignGroupFromShift(group.id, shiftId),
                failure: { visit: group },
              });
            }
            break;
          }
          case shiftItemKindSchema.enum.occurrence: {
            const activityOccurrence = shiftItem.item;
            if (
              activityOccurrence.status !==
                activityOccurrenceStatusSchema.enum.finished &&
              isAssignedToShift(activityOccurrence)
            ) {
              await runUnassign({
                unassign: () =>
                  deallocateShiftsFromActivityOccurrence(
                    [shiftId],
                    activityOccurrence.activityId,
                    activityOccurrence.id,
                  ),
                failure: { activityOccurrence },
              });
            }
            break;
          }
          default:
            throw shiftItem satisfies never;
        }
      }

      // Any failures are optionally handled by the caller
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort aktiviteter och besök från arbetspasset.`} ${deducedError(error)}`,
      );
    },
  });

  return {
    mutateAsync,
    isPending,
  };
};
